import { memo, type ReactElement } from 'react';
import { zaehleBild } from './messung';
import { KANTE_FILTER_ID } from './playback';
import { farbrampe, type Effect } from '@schnitt/shared';

/**
 * The looks, as SVG filters the browser can apply to a video element, so a
 * grade is visible while it is chosen instead of first showing up in the
 * export. The export uses the real .cube files; these are the same maths
 * where the maths is linear (warm, cool, flat) and a close approximation
 * where the LUT bends by luminance (teal-orange, vivid, black and white).
 *
 * Intensity is a plain mix of graded and original, which is exactly what the
 * renderer does with its blend filter.
 */

type Table = number[];

/** Filmic S-curve, the same one scripts/generate-luts.mjs uses. */
function contrastCurve(v: number, amount: number): number {
  const s = v * v * (3 - 2 * v);
  return Math.min(1, Math.max(0, v + (s - v) * amount));
}

function curveTable(amount: number, steps = 17): Table {
  return Array.from({ length: steps }, (_, i) => contrastCurve(i / (steps - 1), amount));
}

interface LookDef {
  /** 4x5 colour matrix, row-major, as feColorMatrix wants it. */
  matrix?: number[];
  /** Per-channel transfer applied after the matrix. */
  table?: Table;
}

const IDENTITY = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

function saturate(s: number): number[] {
  // The standard luminance-preserving saturation matrix.
  const [lr, lg, lb] = [0.2126, 0.7152, 0.0722];
  return [
    lr + (1 - lr) * s, lg - lg * s, lb - lb * s, 0, 0,
    lr - lr * s, lg + (1 - lg) * s, lb - lb * s, 0, 0,
    lr - lr * s, lg - lg * s, lb + (1 - lb) * s, 0, 0,
    0, 0, 0, 1, 0,
  ];
}

export const LOOK_DEFS: Record<string, LookDef> = {
  warm: { matrix: [1.06, 0, 0, 0, 0.02, 0, 1.01, 0, 0, 0, 0, 0, 0.93, 0, 0, 0, 0, 0, 1, 0] },
  cool: { matrix: [0.94, 0, 0, 0, 0, 0, 0.99, 0, 0, 0, 0, 0, 1.07, 0, 0.02, 0, 0, 0, 1, 0] },
  // lerp(v, 0.5, 0.35) = 0.65 v + 0.175, on every channel.
  'flat-log': { matrix: [0.65, 0, 0, 0, 0.175, 0, 0.65, 0, 0, 0.175, 0, 0, 0.65, 0, 0.175, 0, 0, 0, 1, 0] },
  bw: { matrix: saturate(0), table: curveTable(0.35) },
  vivid: { matrix: saturate(1.35), table: curveTable(0.3) },
  // The LUT warms the highlights and cools the shadows by luminance; a matrix
  // cannot do that, so this takes the mid-tone offset and the same curve.
  'teal-orange': { matrix: [1, 0, 0, 0, 0.015, 0, 1, 0, 0, 0.005, 0, 0, 1, 0, 0.025, 0, 0, 0, 1, 0], table: curveTable(0.45) },
  identity: {},
};

export function lookFilterId(clipId: string): string {
  return `look-${clipId}`;
}

/** The lut effect on a clip, if any. */
export function lutOf(effects: Effect[]): Extract<Effect, { type: 'lut' }> | undefined {
  return effects.find((e): e is Extract<Effect, { type: 'lut' }> => e.type === 'lut');
}

/**
 * Der Look als SVG-Filter.
 *
 * Gemerkt (`memo`): beim Abspielen zeichnet sich die Vorschau bei jedem Bild
 * neu, und ohne das würde React sechzigmal in der Sekunde durch die
 * Filterknoten laufen -- für ein Ergebnis, das sich nur ändert, wenn jemand
 * am Look dreht. Genau das meinte 7.27 mit „der Effekt soll nicht bei jedem
 * Bild neu laufen“.
 */
export const LookFilter = memo(function LookFilter({ clipId, name, intensity }: { clipId: string; name: string; intensity: number }): ReactElement | null {
  zaehleBild('look');
  const def = LOOK_DEFS[name];
  if (!def) return null;
  const matrix = (def.matrix ?? IDENTITY).map((v) => Number(v.toFixed(4))).join(' ');
  const table = def.table?.map((v) => Number(v.toFixed(4))).join(' ');
  const k2 = Number(intensity.toFixed(3));
  const k3 = Number((1 - intensity).toFixed(3));
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <filter id={lookFilterId(clipId)} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values={matrix} in="SourceGraphic" result="m" />
          {table ? (
            <feComponentTransfer in="m" result="graded">
              <feFuncR type="table" tableValues={table} />
              <feFuncG type="table" tableValues={table} />
              <feFuncB type="table" tableValues={table} />
            </feComponentTransfer>
          ) : null}
          {/* graded * k2 + original * k3: the renderer's blend, in one line. */}
          <feComposite operator="arithmetic" in={table ? 'graded' : 'm'} in2="SourceGraphic" k1="0" k2={k2} k3={k3} k4="0" />
        </filter>
      </defs>
    </svg>
  );
});

export function gammaFilterId(clipId: string): string {
  return `gamma-${clipId}`;
}

/** Das Gamma eines Clips als Kennlinie (7.133). */
export function gammaOf(effects: Effect[]): number {
  let g = 1;
  for (const fx of effects) if (fx.type === 'colorAdjust') g *= fx.gamma;
  return g;
}

/**
 * Das Gamma als SVG-Filter (7.133).
 *
 * CSS kann kein Gamma; `feComponentTransfer type="gamma"` kann es, und zwar
 * genau als `pow(v, exponent)` je Kanal -- dieselbe Rechnung wie im Shader
 * und in der Kennlinie des Films. Gerechnet wird in sRGB
 * (`colorInterpolationFilters="sRGB"`), sonst rechnete der Browser linear und
 * käme auf andere Zahlen als die anderen zwei.
 */
export const GammaFilter = memo(function GammaFilter({ clipId, gamma }: { clipId: string; gamma: number }): ReactElement | null {
  zaehleBild('gamma');
  if (gamma === 1) return null;
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <filter id={gammaFilterId(clipId)} colorInterpolationFilters="sRGB">
          <feComponentTransfer>
            <feFuncR type="gamma" exponent={Number((1 / gamma).toFixed(6))} />
            <feFuncG type="gamma" exponent={Number((1 / gamma).toFixed(6))} />
            <feFuncB type="gamma" exponent={Number((1 / gamma).toFixed(6))} />
          </feComponentTransfer>
        </filter>
      </defs>
    </svg>
  );
});

export function farbraederFilterId(clipId: string): string {
  return `farbraeder-${clipId}`;
}

/** Die Farbräder an einem Clip, falls welche gestellt sind (S14). */
export function raederOf(effects: Effect[]): Extract<Effect, { type: 'farbraeder' }> | undefined {
  const r = effects.find((e): e is Extract<Effect, { type: 'farbraeder' }> => e.type === 'farbraeder');
  if (!r) return undefined;
  const alle = [...r.tiefen, ...r.mitten, ...r.hoehen];
  // Alles auf null ist kein Effekt, sondern ein Filter, der nichts tut -- und
  // der kostet die Grafikkarte trotzdem jedes Bild.
  return alle.some((v) => Math.abs(v) > 1e-6) ? r : undefined;
}

/**
 * Die Farbräder als SVG-Filter (S14).
 *
 * Eine Kennlinie je Kanal, aus `farbrampe`. Dieselbe Rechnung wie in der
 * Kachelvorschau, damit nicht das eine Bild anders aussieht als das andere;
 * genau wie beim Look ist es eine Näherung, und der Export rechnet mit
 * `colorbalance` nach.
 */
export const FarbraederFilter = memo(function FarbraederFilter({
  clipId,
  tiefen,
  mitten,
  hoehen,
}: {
  clipId: string;
  tiefen: readonly number[];
  mitten: readonly number[];
  hoehen: readonly number[];
}): ReactElement {
  zaehleBild('farbraeder');
  const reihe = (k: number): string =>
    farbrampe(tiefen[k] ?? 0, mitten[k] ?? 0, hoehen[k] ?? 0)
      .map((v) => Number(v.toFixed(4)))
      .join(' ');
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <filter id={farbraederFilterId(clipId)} colorInterpolationFilters="sRGB">
          <feComponentTransfer>
            <feFuncR type="table" tableValues={reihe(0)} />
            <feFuncG type="table" tableValues={reihe(1)} />
            <feFuncB type="table" tableValues={reihe(2)} />
          </feComponentTransfer>
        </filter>
      </defs>
    </svg>
  );
});

/**
 * Der Filter hinter `blur()` für deckende Quellen (7.173, `KANTE_FILTER_ID`):
 * einer für alle Clips, weil er nur vom Kasten abhängt, in dem er steht.
 */
export const KanteFilter = memo(function KanteFilter(): ReactElement {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <filter id={KANTE_FILTER_ID} x="0" y="0" width="1" height="1" colorInterpolationFilters="sRGB">
          <feComponentTransfer>
            <feFuncA type="table" tableValues="1 1" />
          </feComponentTransfer>
        </filter>
      </defs>
    </svg>
  );
});
